import axios from "axios";

/**
 * Validate if the provided string is a valid email format.
 * Checks for the presence of '@', domain, and a dot.
 *
 * @param {string} email
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Sanitize input by removing HTML tags and angle brackets
 * to prevent basic XSS attempts on the frontend.
 *
 * @param {string} text
 * @returns {string}
 */
const sanitizeHtml = (text) => {
  if (!text) return "";
  // Remove anything that looks like an HTML tag <tag> or </tag>
  return text.replace(/<[^>]*>?/gm, "");
};

export default () => ({
  form: {
    name: "",
    email: "",
    message: "",
  },
  maxMessageLength: 500, // Limite máximo de caracteres
  isSubmitting: false,
  isSuccess: false,
  errorMessage: null,

  /**
   * Getter to calculate remaining characters in real-time.
   */
  get remainingCharacters() {
    return this.maxMessageLength - this.form.message.length;
  },

  /**
   * Real-time input handler to sanitize content and enforce limits.
   */
  handleMessageInput() {
    // 1. Sanitize: Remove HTML tags instantly as the user types/pastes
    this.form.message = sanitizeHtml(this.form.message);

    // 2. Clamp length (fallback in case maxlength attribute is bypassed)
    if (this.form.message.length > this.maxMessageLength) {
      this.form.message = this.form.message.substring(0, this.maxMessageLength);
    }
  },

  /**
   * Submit contact form data and Turnstile token to the backend API.
   */
  async submitForm() {
    this.isSubmitting = true;
    this.errorMessage = null;

    // Validate required fields before proceeding
    if (!this.form.name) {
      this.errorMessage = "Por favor, insira seu nome.";
      this.isSubmitting = false;
      document.getElementById("name").focus();
      return;
    }

    if (!this.form.email) {
      this.errorMessage = "Por favor, insira seu e-mail.";
      this.isSubmitting = false;
      document.getElementById("email").focus();
      return;
    }

    // Validate email format
    if (!isValidEmail(this.form.email)) {
      this.errorMessage = "Por favor, insira um e-mail válido.";
      this.isSubmitting = false;
      document.getElementById("email").focus();
      return;
    }

    if (!this.form.message) {
      this.errorMessage = "Por favor, insira sua mensagem.";
      this.isSubmitting = false;
      document.getElementById("message").focus();
      return;
    }

    // Retrieve the Turnstile token generated by the widget
    const turnstileInput = document.querySelector(
      '[name="cf-turnstile-response"]',
    );
    const turnstileToken = turnstileInput ? turnstileInput.value : null;

    if (!turnstileToken) {
      this.errorMessage =
        "Por favor, resolva o desafio de segurança antes de enviar.";
      this.isSubmitting = false;
      return;
    }

    try {
      // Append the token to the payload
      // Sanitize one last time before dispatching just to be absolutely sure
      const payload = {
        name: sanitizeHtml(this.form.name),
        email: this.form.email,
        message: sanitizeHtml(this.form.message),
        cf_turnstile_response: turnstileToken,
      };

      await axios.post("/api/public/contact", payload);

      this.isSuccess = true;
    } catch (error) {
      console.error("Contact Form Error:", error);

      if (error.response && error.response.status === 403) {
        this.errorMessage =
          "Falha na verificação de segurança. Atualize a página e tente novamente.";
      } else {
        this.errorMessage =
          "Não foi possível enviar sua mensagem no momento. Tente novamente mais tarde.";
      }

      // Reset Turnstile widget on failure so the user can try again
      if (window.turnstile) {
        window.turnstile.reset();
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  /**
   * Reset form state to allow sending another message.
   */
  resetForm() {
    this.form.name = "";
    this.form.email = "";
    this.form.message = "";
    this.isSuccess = false;
    this.errorMessage = null;

    // Ensure Turnstile is ready for a new submission
    if (window.turnstile) {
      window.turnstile.reset();
    }
  },
});
